import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Badge,
  Divider,
  Spinner,
  Icon,
  Tooltip,
  useToast,
  useColorModeValue,
  ScaleFade,
  SlideFade,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaBookOpen, FaPlus, FaSave, FaTimes, FaLayerGroup, FaGraduationCap, FaInfoCircle } from 'react-icons/fa';
import { addChapter } from '../services/courseService';

function NewChapterToCourse({ isOpen, onClose, courseId, onChapterAdded }) {
  const [newChapterName, setNewChapterName] = useState('');
  const [chapterDescription, setChapterDescription] = useState('');
  const [numberOfParts, setNumberOfParts] = useState(1);
  const [partNames, setPartNames] = useState(['']);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Responsive values
  const modalSize = useBreakpointValue({ base: "full", md: "2xl", lg: "3xl" });
  const headerSize = useBreakpointValue({ base: "lg", md: "xl", lg: "2xl" });
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });

  // Color mode values
  const bgGradient = useColorModeValue(
    'linear(135deg, orange.500 0%, red.600 50%, pink.500 100%)',
    'linear(135deg, orange.600 0%, red.700 50%, pink.600 100%)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const primaryColor = '#032D7F';

  const handlePartCountChange = (value) => {
    setNumberOfParts(value);
    const updatedPartNames = Array(value).fill('').map((_, index) => 
      partNames[index] || ''
    );
    setPartNames(updatedPartNames);
  };

  const handlePartNameChange = (index, value) => {
    const updatedPartNames = [...partNames];
    updatedPartNames[index] = value;
    setPartNames(updatedPartNames);
  };

  const handleSaveChapter = async () => {
    // Validation
    if (!newChapterName.trim()) {
      toast({
        title: "خطأ في البيانات ❌",
        description: "اسم  محور مطلوب",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-left"
      });
      return;
    }

    // Check if all part names are filled
    const emptyParts = partNames.some(partName => !partName.trim());
    if (emptyParts) {
      toast({
        title: "خطأ في البيانات ❌",
        description: "يرجى ملء جميع أسماء الأجزاء",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-left"
      });
      return;
    }

    const newChapter = {
      nom: newChapterName.trim(),
      description: chapterDescription.trim(),
      parties: partNames
        .filter((partName) => partName.trim() !== '')
        .map(el => ({ 
          name: el.trim(), 
          description: `جزء من محور ${newChapterName.trim()}`,
          videos: [], 
          attachments: [] 
        })),
    };

    try {
      setLoading(true);
      const response = await addChapter(courseId, newChapter);

      // Success animation delay
      setTimeout(() => {
        console.log('تمت إضافة  محور:', response.data);
        onChapterAdded();
        resetForm();
        onClose();
        
        toast({
          title: "تم الحفظ بنجاح! 🎉",
          description: `تم إضافة  محور "${newChapterName}" مع ${numberOfParts} ${numberOfParts > 1 ? 'أجزاء' : 'جزء'}`,
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "top-left"
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('خطأ في إضافة  محور:', error);
      setLoading(false);
      toast({
        title: "فشل في الحفظ ❌",
        description: error.message || "حدث خطأ أثناء إضافة  محور",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-left"
      });
    }
  };

  const resetForm = () => {
    setNewChapterName('');
    setChapterDescription('');
    setNumberOfParts(1);
    setPartNames(['']);
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal  
    
      isOpen={isOpen} 
      onClose={handleClose} 
      isCentered
      size={modalSize}
      closeOnOverlayClick={!loading}
    >
      <ModalOverlay 
        backdropFilter="blur(15px)" 
        bg="blackAlpha.600"
        animation="fadeIn 0.3s ease-out"
      />
      <ModalContent 
      
        dir="rtl"
        borderRadius="3xl" 
        overflow="hidden" 
        shadow="2xl"
        bg={cardBg}
        border="1px solid"
        borderColor="whiteAlpha.200"
        transform={loading ? "scale(1.02)" : "scale(1)"}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <ModalHeader
          bgGradient={bgGradient}
          color="white"
          textAlign="center"
          py={10}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgGradient: "linear(45deg, transparent 30%, whiteAlpha.100 50%, transparent 70%)",
            transform: loading ? "translateX(100%)" : "translateX(-100%)",
            transition: "transform 1s ease-in-out",
          }}
        >
          <VStack spacing={4} position="relative" zIndex={1}>
            <Box
            
              p={4}
              bg="whiteAlpha.200"
              borderRadius="2xl"
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor="whiteAlpha.300"
              transform={loading ? "rotate(360deg) scale(1.1)" : "rotate(0deg) scale(1)"}
              transition="all 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
            >
              <FaBookOpen size={40} />
            </Box>
            <VStack spacing={2}>
              <Text 
                fontSize={headerSize} 
                fontWeight="bold" 
                textShadow="0 2px 4px rgba(0,0,0,0.3)"
              >
                {loading ? "جاري إضافة  محور..." : "إضافة محور جديد"}
              </Text>
              <HStack spacing={2}>
                <Icon as={FaGraduationCap} />
                <Text 
                  fontSize="md" 
                  opacity={0.9}
                  textAlign="center"
                >
                  {loading ? "يرجى الانتظار حتى اكتمال العملية" : "إنشاء محور جديد مع أجزائه"}
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </ModalHeader>
        
        <ModalCloseButton 
          color="white" 
          size="lg"
          isDisabled={loading}
          _hover={{
            bg: "whiteAlpha.200",
            transform: "scale(1.1)"
          }}
          transition="all 0.2s ease"
        />

        <ModalBody p={{ base: 6, md: 10 }}>
          <VStack spacing={8} >
            {/* Chapter Name */}
            <ScaleFade in={isOpen} delay={0.1}>
              <FormControl> 
                <FormLabel 
                  color={textColor} 
                  fontWeight="bold"
                  fontSize="lg"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w={2} h={2} bg="orange.500" borderRadius="full" />
                  اسم  محور
                  <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="full">
                    مطلوب
                  </Badge>
                </FormLabel>
                <Input
                
                  placeholder="أدخل اسم  محور"
                  value={newChapterName}
                  onChange={(e) => setNewChapterName(e.target.value)}
                  dir="rtl"
                  
                  borderRadius="2xl"
                  borderWidth="2px"
                  borderColor={newChapterName.trim() ? "green.300" : "gray.300"}
                  bg={newChapterName.trim() ? "green.50" : "gray.50"}
                  py={6}
                  fontSize="lg"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${primaryColor}20`,
                    bg: "white",
                    transform: "translateY(-2px)"
                  }}
                  _hover={{
                    borderColor: "gray.400",
                    bg: "white"
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  isDisabled={loading}
                />
              </FormControl>
            </ScaleFade>

            {/* Chapter Description */}
            <SlideFade in={isOpen} offsetY="20px" delay={0.2}>
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="bold"
                  fontSize="lg"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w={2} h={2} bg="red.500" borderRadius="full" />
                  وصف  محور
                  <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="full">
                    اختياري
                  </Badge>
                </FormLabel>
                <Textarea
                  placeholder="أدخل وصف مختصر للمحور (اختياري)"
                  value={chapterDescription}
                  onChange={(e) => setChapterDescription(e.target.value)}
                  dir="rtl"
                  borderRadius="2xl"
                  borderWidth="2px"
                  borderColor="gray.300"
                  bg="gray.50"
                  py={4}
                  fontSize="lg"
                  rows={3}
                  resize="vertical"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${primaryColor}20`,
                    bg: "white",
                    transform: "translateY(-2px)"
                  }}
                  _hover={{
                    borderColor: "gray.400",
                    bg: "white"
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  isDisabled={loading}
                />
              </FormControl>
            </SlideFade>

            {/* Number of Parts */}
            <SlideFade in={isOpen} offsetY="20px" delay={0.3}>
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="bold"
                  fontSize="lg"
                  mb={4}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w={2} h={2} bg="pink.500" borderRadius="full" />
                  عدد الأجزاء
                  <Badge
                    colorScheme="orange"
                    fontSize="md"
                    px={3}
                    py={1}
                    borderRadius="full"
                    ml={2}
                  >
                    {numberOfParts} {numberOfParts > 1 ? 'أجزاء' : 'جزء'}
                  </Badge>
                </FormLabel>
                <Box
                  p={6}
                  bg="gray.50"
                  borderRadius="2xl"
                  border="2px solid"
                  borderColor="gray.300"
                  _hover={{
                    borderColor: "gray.400",
                    bg: "white"
                  }}
                  transition="all 0.3s ease"
                >
                  <Slider
                    value={numberOfParts}
                    min={1}
                    max={20}
                    step={1}
                    onChange={handlePartCountChange}
                    isDisabled={loading}
                  >
                    <SliderTrack bg="gray.300" h={3} borderRadius="full">
                      <SliderFilledTrack 
                        bgGradient="linear(to-r, orange.400, red.400)" 
                        borderRadius="full"
                      />
                    </SliderTrack>
                    <SliderThumb 
                      boxSize={6}
                      bg="white"
                      border="3px solid"
                      borderColor="pink.400"
                      _focus={{
                        boxShadow: "0 0 0 3px pink.200"
                      }}
                    >
                      <Icon as={FaLayerGroup} color="pink.400" />
                    </SliderThumb>
                  </Slider>
                  <HStack justify="space-between" mt={3}>
                    <Text fontSize="sm" color="gray.600">1 جزء</Text>
                    <Text fontSize="sm" color="gray.600">20 جزء</Text>
                  </HStack>
                </Box>
              </FormControl>
            </SlideFade>

            <Divider borderColor={borderColor} />

            {/* Parts Names */}
            <VStack spacing={6} w="full">
              <HStack spacing={2}>
                <Icon as={FaLayerGroup} color="orange.500" />
                <Text 
                  fontSize="lg" 
                  fontWeight="bold" 
                  color={textColor}
                >
                  أسماء الأجزاء
                </Text>
                <Tooltip 
                  label="يجب ملء جميع أسماء الأجزاء قبل الحفظ" 
                  placement="top"
                >
                  <Icon as={FaInfoCircle} color="blue.400" />
                </Tooltip>
              </HStack>
              
              {partNames.map((partName, index) => (
                <SlideFade 
                  key={index} 
                  in={isOpen} 
                  offsetY="20px" 
                  delay={0.4 + (index * 0.1)}
                >
                  <FormControl w="full">
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Badge
                        colorScheme="orange"
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {index + 1}
                      </Badge>
                      اسم الجزء {index + 1}
                    </FormLabel>
                    <Input
                      placeholder={`أدخل اسم الجزء ${index + 1}`}
                      value={partName}
                      onChange={(e) => handlePartNameChange(index, e.target.value)}
                      dir="rtl"
                      borderRadius="2xl"
                      borderWidth="2px"
                      borderColor={partName.trim() ? "green.300" : "gray.300"}
                      bg={partName.trim() ? "green.50" : "gray.50"}
                      py={6}
                      fontSize="lg"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${primaryColor}20`,
                        bg: "white",
                        transform: "translateY(-2px)"
                      }}
                      _hover={{
                        borderColor: "gray.400",
                        bg: "white"
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      isDisabled={loading}
                    />
                  </FormControl>
                </SlideFade>
              ))}
            </VStack>

            {/* Loading State */}
            {loading && (
              <ScaleFade in={loading}>
                <VStack spacing={4} py={6}>
                  <Spinner
                    size="xl"
                    color="indigo.500"
                    thickness="4px"
                  />
                  <Text color={textColor} fontSize="lg" fontWeight="medium">
                    جاري إضافة  محور...
                  </Text>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    يرجى الانتظار حتى اكتمال العملية
                  </Text>
                </VStack>
              </ScaleFade>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter p={{ base: 6, md: 10 }} pt={0}>
          <HStack spacing={4} w="full" justify="center">
            <Tooltip label="إلغاء العملية" placement="top">
              <Button 
                variant="outline"
                onClick={handleClose}
                borderRadius="2xl"
                borderWidth="2px"
                px={8}
                py={6}
                fontSize="lg"
                isDisabled={loading}
                leftIcon={<FaTimes />}
                _hover={{
                  bg: "gray.100",
                  transform: loading ? "none" : "translateY(-2px)",
                  shadow: "lg"
                }}
                transition="all 0.3s ease"
              >
                إلغاء
              </Button>
            </Tooltip>
            
            <Tooltip label="حفظ  محور الجديد" placement="top">
              <Button 
                bg="blue"
                color="white"
                onClick={handleSaveChapter} 
                isLoading={loading}
                loadingText="جاري الحفظ..."
                isDisabled={!newChapterName.trim() || partNames.some(partName => !partName.trim()) || loading}
                borderRadius="2xl"
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="bold"
                leftIcon={!loading ? <FaSave /> : undefined}
                _hover={{
                  bgGradient: "linear(135deg, indigo.600 0%, purple.700 50%, pink.600 100%)",
                  transform: loading ? "none" : "translateY(-2px)",
                  shadow: "2xl"
                }}
                _active={{
                  transform: "scale(0.98)"
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                {loading ? "جاري الحفظ..." : "حفظ  محور"}
              </Button>
            </Tooltip>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default NewChapterToCourse;

